import { PLAYBOOK_SCOPE_DESCRIPTION_MAX_LENGTH } from "@repo/app-types/entities/playbook";
import { Size } from "@repo/ui/helpers/helpers";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import { useState, type FC } from "react";
import { DiffableField } from "../diffableField.js";

const DESCRIPTION =
  "Describe which customer interactions this playbook is for, such as the kind of call it covers and what it leaves out.";

const PLACEHOLDER =
  "Inbound sales calls from prospective customers. Excludes billing questions, support requests, and calls that end before the customer states a need.";

export interface ScopeSettingsFormProps {
  /** Trimmed and non-empty, or null when the playbook has no scope. */
  description: string | null;
  onUpdateDescription: (description: string | null) => void;
  isReadOnly: boolean;
  /**
   * Live/published description. When provided (i.e. not `undefined`) and it
   * differs from the current one, a "See changes" toggle diffs the draft
   * against it.
   */
  liveDescription?: string | null;
  /** Show a draft dot next to the label when there are unpublished changes. */
  hasUnpublishedChanges?: boolean;
}

function normalizeScopeDescription(text: string): string | null {
  const trimmedText = text.trim();
  return trimmedText === "" ? null : trimmedText;
}

export const ScopeSettingsForm: FC<ScopeSettingsFormProps> = (props) => {
  const {
    description,
    onUpdateDescription,
    isReadOnly,
    liveDescription,
    hasUnpublishedChanges,
  } = props;

  // The draft holds the trimmed description, so the text being typed lives
  // here: feeding the trimmed value back would eat a trailing space or newline
  // mid-sentence. It is replaced only when the description changes to
  // something the text doesn't already say (a discard, a delete, a reload).
  const [text, setText] = useState(description ?? "");
  const [syncedDescription, setSyncedDescription] = useState(description);
  if (description !== syncedDescription) {
    setSyncedDescription(description);
    if (normalizeScopeDescription(text) !== description) {
      setText(description ?? "");
    }
  }

  const handleTextChange = (newText: string) => {
    setText(newText);
    onUpdateDescription(normalizeScopeDescription(newText));
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DiffableField
        label={
          <Label size={Size.SMALL}>
            <span className="flex items-center gap-1.5">
              Scope
              {hasUnpublishedChanges && (
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              )}
            </span>
          </Label>
        }
        value={description ?? ""}
        savedValue={
          liveDescription === undefined ? undefined : (liveDescription ?? "")
        }
        diffClassName="min-h-0 flex-1 overflow-y-auto"
      >
        <Textarea
          placeholder={PLACEHOLDER}
          value={text}
          onChange={handleTextChange}
          fillHeight
          maxLength={PLAYBOOK_SCOPE_DESCRIPTION_MAX_LENGTH}
          isDisabled={isReadOnly}
          description={DESCRIPTION}
        />
      </DiffableField>
    </div>
  );
};
